import _ from 'lodash'
import moment from 'moment'

import { CRUD, CrudModel } from '../../classes'
import { AppointmentModel, AuthorizationRuleAPI, InsuranceProviderLookup, PatientInsurance, PatientUploadedFile } from '..'
import { AUTHORIZATION_STATUS } from '../auth-status'
import { AvailityServiceReview } from '../availity'
import { PortalModel, PortalQuestion } from '../portal'
import { ProductData, ProductModel } from '../product'
import { AsyncPullRequest, AuthorizationData, AuthorizationProcedureData } from './authorization.data'
import { AuthorizationPortalData } from './authorization-portal'

export type LimitationQuantityType = 'visit' | 'hour' | 'day' | 'dosage' | 'FL'

export interface Limitations {
  quantity: number
  quantityType: LimitationQuantityType
}

export enum OutdatedFlags {
  ConflictDates = 'ConflictDates',
  MissingProcedures = 'MissingProcedures',
  WrongFacility = 'WrongFacility',
}

export interface AuthorizationStatusPullData {
  nextPullAt?: moment.Moment
  lastPulledAt?: moment.Moment
  lastPullAttempt?: moment.Moment
  lastPulledByName?: string
  canPullStatus: boolean
  sourceApi?: AuthorizationRuleAPI
  updatedBy?: string
  createdBy?: string
}

export const PENDING_AUTHORIZATION_STATUSES = [
  AUTHORIZATION_STATUS.INITIATED_CASE_INSURANCE,
  AUTHORIZATION_STATUS.INITIATED_CASE_MEDICAL,
  AUTHORIZATION_STATUS.APPROVED,
]

export interface ModifiedByData {
  name?: string
}

@CRUD({
  base: '/authorizations',
})
export class AuthorizationModel extends CrudModel {
  patientId: string
  accountIds: Set<string>
  episodeIds: Set<string>
  appointmentIds: Set<string>
  expiredAppointmentIds: Set<string>
  manuallyEditedFields: Set<string>
  outdatedAppointmentIds: Set<string>
  ignoredOutdatedIds: Set<string>
  insuranceId?: string
  patientInsurance?: PatientInsurance
  carrierLookupId?: string
  carrierId?: string
  facilityId?: string
  facilityNPI?: string
  referrerId?: string
  providerIds?: string[]
  procedureCodes?: string[]
  productIds: string[]
  products: ProductModel[]
  productNames: string[]
  diagnosisCodes?: string[]
  number: string
  startAt?: moment.Moment
  endAt?: moment.Moment
  referenceNumber?: string
  manualNotes?: string
  note?: string
  // cannot be typed because the structure depends on the insurance carrier
  fullAuthorizationObject?: AvailityServiceReview.Review
  limitations?: Limitations
  status: AUTHORIZATION_STATUS
  proceduresData?: Record<string, AuthorizationProcedureData>
  customFields?: Record<string, any>
  attachments?: PatientUploadedFile[]
  awaitingAsyncPullResult?: AsyncPullRequest | null
  nextPullAt?: moment.Moment
  lastPullAttempt?: moment.Moment
  lastPulledAt?: moment.Moment
  lastPulledBy?: string
  lastPulledByName?: string
  outdatedFlags?: OutdatedFlags[]
  portals?: PortalModel[]
  questions?: PortalQuestion[]
  portalData?: AuthorizationPortalData
  sourceApi?: AuthorizationRuleAPI
  updatedBy?: ModifiedByData
  createdBy?: ModifiedByData

  // Extended Data
  carrierLookup?: InsuranceProviderLookup

  constructor(data: AuthorizationData) {
    super(data, true)

    this.fullAuthorizationObject = data.fullAuthorizationObject
    this.note =
      data.note ??
      data.fullAuthorizationObject?.procedures
        ?.flatMap((p) => p.notes ?? [])
        .map((n) => {
          const fields: string[] = []
          if (n.type) {
            fields.push(`Type: ${n.type}`)
          }
          if (n.typeCode) {
            fields.push(`Code: ${n.typeCode}`)
          }
          if (n.message) {
            fields.push(`Message: ${n.message}`)
          }
          return fields.join(', ')
        })
        .join('\n')
    if (data.startAt) {
      this.startAt = this.transformDate(data.startAt, false)
    }
    if (data.endAt) {
      this.endAt = this.transformDate(data.endAt, false)
    }
    if (data.nextPullAt) {
      this.nextPullAt = this.transformDate(data.nextPullAt, false)
    }
    if (data.lastPullAttempt) {
      this.lastPullAttempt = this.transformDate(data.lastPullAttempt, false)
    }
    if (data.lastPulledAt) {
      this.lastPulledAt = this.transformDate(data.lastPulledAt, false)
    }

    if (data.portals) {
      this.portals = data.portals.map(portal => new PortalModel(portal))
    }

    if (data.patientInsurance) {
      this.patientInsurance = new PatientInsurance(data.patientInsurance)
    }

    this.products = (data.products || []).map((product: ProductData) => new ProductModel(product))
    this.productIds = data.productIds ?? this.products.map(product => product.id)
    this.productNames = this.products.map((product: ProductModel) => product.name)
    this.procedureCodes = data.procedureCodes ?? this.fullAuthorizationObject?.procedures?.map(p => p.code)
    this.products = (data.products || []).map(
      (product: ProductData) => new ProductModel(product),
    )
    this.productIds = data.productIds ?? this.products.map((product) => product.id)
    this.productNames = this.products.map((product: ProductModel) => product.name)
    this.procedureCodes = data.procedureCodes ?? this.fullAuthorizationObject?.procedures?.map((p) => p.code)
    this.accountIds = new Set(data.accountIds ?? [])
    this.appointmentIds = new Set(data.appointmentIds ?? [])
    this.episodeIds = new Set(data.episodeIds ?? [])
    this.outdatedAppointmentIds = new Set(data.outdatedAppointmentIds ?? [])
    this.expiredAppointmentIds = new Set(data.expiredAppointmentIds ?? [])
    this.manuallyEditedFields = new Set(data.manuallyEditedFields ?? [])
    this.ignoredOutdatedIds = new Set(data.ignoredOutdatedIds ?? [])
    this.proceduresData = data.proceduresData ?? {}
  }

  get dateFrom(): string | undefined {
    return this.startAt?.format('YYYY-MM-DD')
  }

  get dateTo(): string | undefined {
    return this.endAt?.format('YYYY-MM-DD')
  }

  get statusPullData() {
    const data: AuthorizationStatusPullData = {
      nextPullAt: this.nextPullAt,
      lastPulledAt: this.lastPulledAt,
      lastPullAttempt: this.lastPullAttempt,
      canPullStatus: PENDING_AUTHORIZATION_STATUSES.includes(this.status) && !!this.sourceApi && !!this.fullAuthorizationObject,
      sourceApi: this.sourceApi,
      updatedBy: this.updatedBy?.name ?? '',
      createdBy: this.createdBy?.name ?? '',
    }

    data.lastPulledByName = this.lastPulledByName ?? this.lastPulledBy

    return data
  }

  get procedureCodesDisplay() {
    const codes = new Set<string>()
    if (this.procedureCodes?.length) {
      this.procedureCodes.forEach((c) => codes.add(c))
    }

    if (this.products?.length) {
      this.products.map((p) => p.getCPTCodes().forEach((c) => codes.add(c)))
    }

    return [...codes]
  }

  get hasDateConflict() {
    return this.outdatedFlags?.includes(OutdatedFlags.ConflictDates) ?? false
  }

  get hasMissingProcedures() {
    return this.outdatedFlags?.includes(OutdatedFlags.MissingProcedures) ?? false
  }

  get hasWrongFacility() {
    return this.outdatedFlags?.includes(OutdatedFlags.WrongFacility) ?? false
  }

  get identifier(): string {
    return this.number || this.referenceNumber || 'N/A'
  }

  public isAssociatedWithAppointment(appointment?: AppointmentModel): boolean {
    if (!appointment) {
      return false
    }

    // consider the Authorization associated with the Appointment if the Authorization
    // is directly linked via appointmentIds or indirectly linked via the Patient Account
    if (appointment.accountId && this.accountIds?.has(appointment.accountId)) {
      return true
    }

    return this.appointmentIds.has(appointment.id)
  }

  public get isComplete(): boolean {
    return [
      AUTHORIZATION_STATUS.APPROVED,
      AUTHORIZATION_STATUS.PARTIALLY_APPROVED,
      AUTHORIZATION_STATUS.DENIED,
      AUTHORIZATION_STATUS.INELIGIBLE,
      AUTHORIZATION_STATUS.EXPIRED,
      AUTHORIZATION_STATUS.CANCELLED,
      AUTHORIZATION_STATUS.NO_ACTION_REQUIRED,
    ].includes(this.status)
  }

  override toJSON(): any {
    return _.extend(super.toJSON(), {
      patientId: this.patientId,
      appointmentIds: [...this.appointmentIds],
      accountIds: [...this.accountIds],
      episodeIds: [...this.episodeIds],
      facilityId: this.facilityId,
      facilityNPI: this.facilityNPI,
      insuranceId: this.insuranceId,
      carrierLookupId: this.carrierLookupId,
      carrierId: this.carrierId,
      fullAuthorizationObject: this.fullAuthorizationObject,
      productIds: this.productIds,
      number: this.number,
      startAt: this.startAt,
      endAt: this.endAt,
      referenceNumber: this.referenceNumber,
      referrerId: this.referrerId,
      status: this.status,
      note: this.note,
      manualNotes: this.manualNotes,
      limitations: this.limitations,
      manuallyEditedFields: [...this.manuallyEditedFields],
    })
  }
}
